import { Link } from "react-router-dom";
import { ShieldCheck, EyeOff, HeartHandshake, MessageCircleHeart, Users, Sparkles, Quote, ArrowRight } from "lucide-react";
import {
  HubPage, Container, SectionHead, CommunityCTA, BetaCTA, Band, HeroMedia, Seo,
} from "../components/hub/HubKit";
import { COMMUNITY_PROMPTS, COMMUNITY_SPACES, PAGE_IMAGES, COMMUNITY_STORIES } from "../data/hub";
import { track } from "../lib/analytics";

const HOW_IT_WORKS = [
  { icon: Users, title: "Join anonymously", note: "Create a private profile. Share as much or as little as you like — no real name required." },
  { icon: MessageCircleHeart, title: "Find your spaces", note: "Browse gentle, topic-based spaces and jump into the conversations that fit where you are." },
  { icon: HeartHandshake, title: "Give and get support", note: "Offer encouragement, ask for a boost, or simply be heard. Empathy comes before algorithms." },
];

export default function Community() {
  return (
    <HubPage testid="community-page">
      <Seo
        title="The HEARD Community — Anonymous, Human Support"
        description="Discover the HEARD app community: anonymous, kind, and human. Everyday wins, a Just Venting space, gentle encouragement, and privacy that comes first."
      />

      <Band tone="sage" className="border-b border-line/60">
        <Container className="pt-14 pb-12 sm:pt-20">
          <div className="grid items-center gap-8 lg:grid-cols-[1fr_0.9fr]">
            <SectionHead
              overline="Community"
              title="Support that actually feels human."
              sub="HEARD is a private, anonymous community where empathy comes before algorithms. This page is an introduction — real conversations happen inside the HEARD app."
            />
            <HeroMedia src={PAGE_IMAGES.community} alt="A warm, diverse group of friends sharing a candid moment together in golden light" eager className="aspect-[16/10] lg:aspect-[5/4]" />
          </div>
        </Container>
      </Band>

      {/* Community Stories */}
      <Band tone="white" className="border-b border-line/60 py-14 sm:py-16">
        <Container>
          <div data-testid="community-stories">
            <span className="text-[0.7rem] font-medium uppercase tracking-[0.28em] text-[#B98A4E]">Community Stories</span>
            <h2 className="serif-display mt-3 text-2xl sm:text-3xl text-charcoal">Real stories from the HEARD family</h2>
            <p className="mt-3 max-w-2xl text-base leading-relaxed text-ink/70">
              Real-life stories about everyday experiences, support, resilience, family, joy, and showing up for one another — shared with permission.
            </p>
            <Link to="/share-your-story" data-testid="share-your-story-link" className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-sage-deep hover:gap-2.5 transition-all">
              Share your story with HEARD <ArrowRight size={15} />
            </Link>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
              {COMMUNITY_STORIES.map((s) => (
                <Link
                  key={s.slug}
                  to={`/community-stories/${s.slug}`}
                  onClick={() => track("story_opened", s.slug)}
                  data-testid={`community-story-card-${s.slug}`}
                  className="group flex flex-col overflow-hidden rounded-[1.6rem] border border-line bg-cream/60 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(47,75,57,0.1)]"
                >
                  {s.image && (
                    <div className="aspect-[16/9] overflow-hidden bg-cream-deep/40">
                      <img src={s.image} alt={s.subtitle || s.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col p-6 sm:p-7">
                    <span className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-sage">HEARD Community Story</span>
                    <h3 className="serif-display mt-2 text-xl text-charcoal">{s.title}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-ink/70">{s.excerpt}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-sage-deep group-hover:gap-2.5 transition-all">
                      Read Troy's Story <ArrowRight size={15} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </Container>
      </Band>

      {/* What HEARD is + Just Venting */}
      <Container className="py-14 sm:py-20">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.2fr_1fr] lg:items-stretch" data-testid="what-heard-is">
          <div className="rounded-[1.6rem] border border-line bg-white/70 p-7 sm:p-10">
            <h2 className="serif-display text-2xl sm:text-[2rem] text-charcoal">What HEARD is</h2>
            <p className="mt-4 text-base leading-relaxed text-ink/75">
              HEARD is a kinder kind of online community — a place to be honest about real life without performance, judgment,
              or the pressure of a public feed. It's built for the ordinary moments: the wins worth celebrating, the hard days,
              and everything in between.
            </p>
            <p className="mt-4 text-base leading-relaxed text-ink/75">
              The website is where you discover and read. The <span className="font-medium text-charcoal">app is where you take part</span> —
              connecting with people who simply want to listen and be listened to.
            </p>
          </div>
          <div className="flex flex-col justify-center rounded-[1.6rem] bg-sage-deep/95 p-7 text-cream sm:p-10" data-testid="just-venting">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cream/15 text-cream">
              <Sparkles size={20} strokeWidth={1.8} />
            </span>
            <h3 className="serif-display mt-4 text-xl sm:text-2xl">The "Just Venting" space</h3>
            <p className="mt-3 text-sm leading-relaxed text-cream/85">
              Sometimes you don't want advice or solutions — you just need to let it out and be heard. Our Just Venting space is a
              no-fixing zone: say the hard thing, set it down, and feel a little lighter. No pressure to explain or tidy it up.
            </p>
          </div>
        </div>
      </Container>

      {/* How it works */}
      <Band tone="white" className="border-y border-line/60 py-14 sm:py-20">
        <Container>
          <div data-testid="how-it-works">
            <h2 className="serif-display text-2xl text-charcoal">How the community works</h2>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
              {HOW_IT_WORKS.map((s, i) => (
                <div key={s.title} className="relative rounded-[1.4rem] border border-line bg-cream/60 p-6">
                  <span className="absolute right-5 top-5 serif-display text-3xl text-sage/25">{i + 1}</span>
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sage/12 text-sage-deep">
                    <s.icon size={20} strokeWidth={1.8} />
                  </span>
                  <h3 className="mt-4 font-serif text-lg font-semibold text-heading">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/70">{s.note}</p>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </Band>

      {/* Community spaces */}
      <Container className="py-14 sm:py-20">
        <div data-testid="community-spaces">
          <h2 className="serif-display text-2xl text-charcoal">Examples of community spaces</h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink/70">
            A glimpse of the kinds of spaces members gather in — from everyday wins to the harder days.
          </p>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {COMMUNITY_SPACES.map((s, i) => (
              <div key={s.title} className="group rounded-[1.4rem] border border-line bg-white/75 p-6 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(47,75,57,0.1)]" data-testid="community-space">
                <span className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-sage">Space</span>
                <h3 className="mt-2 font-serif text-lg font-semibold text-heading">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink/70">{s.note}</p>
              </div>
            ))}
          </div>
        </div>
      </Container>

      {/* Prompts */}
      <Band tone="deep" className="py-14 sm:py-20">
        <Container>
          <div data-testid="community-prompts">
            <h2 className="serif-display text-2xl text-charcoal">Conversations worth having</h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink/70">
              Example prompts members explore together — shared safely here, never real or private content.
            </p>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {COMMUNITY_PROMPTS.map((p) => (
                <div key={p} className="flex gap-3 rounded-2xl border border-line bg-white/70 px-5 py-4 text-sm text-ink/80" data-testid="community-prompt">
                  <Quote size={16} className="mt-0.5 shrink-0 text-sage/60" />
                  <span>{p}</span>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </Band>

      {/* Privacy */}
      <Container className="py-14 sm:py-20">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2" data-testid="privacy-positioning">
          <div className="flex items-start gap-4 rounded-[1.4rem] border border-line bg-white/70 p-6 sm:p-7">
            <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-sage/12 text-sage-deep">
              <EyeOff size={20} strokeWidth={1.8} />
            </span>
            <div>
              <h3 className="font-serif text-lg font-semibold text-heading">Anonymous by design</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink/70">
                You choose what to share. No real name is required, so you can be honest without fear of judgment from the people in your daily life.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4 rounded-[1.4rem] border border-line bg-white/70 p-6 sm:p-7">
            <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-sage/12 text-sage-deep">
              <ShieldCheck size={20} strokeWidth={1.8} />
            </span>
            <div>
              <h3 className="font-serif text-lg font-semibold text-heading">Safety comes first</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink/70">
                Kindness is the standard here. Read our{" "}
                <Link to="/community-guidelines" className="text-sage-deep underline" data-testid="link-community-guidelines">Community Guidelines</Link> and{" "}
                <Link to="/child-safety" className="text-sage-deep underline" data-testid="link-child-safety">Child Safety Standards</Link>.
              </p>
            </div>
          </div>
        </div>
      </Container>

      <Band tone="deep" className="py-14 sm:py-20">
        <Container>
          <BetaCTA />
          <div className="mt-8"><CommunityCTA /></div>
        </Container>
      </Band>
    </HubPage>
  );
}
